import { Button, Form, FormInstance, Layout, List, Spin, TabsProps, Tooltip, Typography } from 'antd';
import { UserGroupFilledIcon } from 'assets';
import { TabButtons } from 'components/common';
import { FormItem } from 'components/common/form-item';
import { TreeCheckboxPermissions } from 'components/permissions';
import { CheckboxRoles } from 'components/roles';
import { uniq, uniqBy } from 'lodash';
import { messages, permissionsMessages, rolesMessages, usersMessages } from 'messages';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  useGetRolesPermissionsQuery,
  useGetUserPermissionsQuery,
  useGetUserQuery,
  useLazyGetRolesPermissionsQuery,
  useUpdateUserMutation
} from 'services';
import { PermissionCompactDto, RoleCompactDto } from 'types';

enum TABS {
  PERMISSIONS = 'permissions',
  ROLES = 'roles'
}

export type UserUpdatePermissionsFormProps = {
  onChangeLoading?: (value: boolean) => void;
  onChangeSuccess?: () => void;
  userId?: number;
};

export type UserUpdatePermissionsFormRefProps = {
  form: FormInstance<UserUpdatePermissionsFormType>;
  isLoading: boolean;
};
export type UserUpdatePermissionsFormType = {
  permissions: PermissionCompactDto[];
  roles: RoleCompactDto[];
};
const UserUpdatePermissionsForm = forwardRef<UserUpdatePermissionsFormRefProps, UserUpdatePermissionsFormProps>(
  ({ userId, onChangeLoading, onChangeSuccess }, ref) => {
    useImperativeHandle(ref, () => ({
      form: form,
      isLoading: isLoadingUpdate
    }));

    const { data: userInfoRes } = useGetUserQuery(userId!, {
      skip: !userId
    });
    const { data: userRes, isLoading } = useGetUserPermissionsQuery(userId!, {
      skip: !userId,
      refetchOnMountOrArgChange: true
    });
    const [getRolePermissions, { data: rolePermissionsRes }] = useLazyGetRolesPermissionsQuery();

    const userInfo = userInfoRes?.data;
    const userPermissions = userRes?.data;
    const rolePermissions = rolePermissionsRes?.data.rows.flatMap((userGroup) => userGroup.permissions) || [];
    const userGroupPermissions = userPermissions?.userGroups?.flatMap((userGroup) => userGroup.permissions) || [];
    const userGroupPermissionIds = userGroupPermissions.map((p) => p.permissionId);
    const userGroupRoleIds =
      userPermissions?.userGroups?.flatMap((userGroup) => userGroup.roles).map((r) => r.roleId) || [];

    const { data: userGroupRolePermissionRes } = useGetRolesPermissionsQuery(
      {
        roleIds: userGroupRoleIds || [0]
      },
      {
        skip: !userGroupRoleIds || userGroupRoleIds.length === 0
      }
    );
    const userGroupRoles = userGroupRolePermissionRes?.data.rows || [];
    const userGroupRolePermissions = userGroupRoles.flatMap((r) => r.permissions);
    const userGroupRolePermissionIds = uniq(userGroupRolePermissions.map((p) => p.permissionId));

    const [selectedTab, setSelectedTab] = useState<TABS>(TABS.ROLES);

    const [form] = Form.useForm<UserUpdatePermissionsFormType>();
    const [onUpdate, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();
    const onFinish = (values: UserUpdatePermissionsFormType) => {
      if (!userId) return;
      onUpdate({
        userId,
        permissionIds: values.permissions.map((o) => o.permissionId),
        roleIds: values.roles.map((o) => o.roleId)
      })
        .unwrap()
        .then(() => {
          onChangeSuccess?.();
        });
    };
    useEffect(() => {
      if (userPermissions && userId) {
        form.setFieldsValue({
          permissions: userPermissions.permissions,
          roles: userPermissions.roles
        });
        if (userPermissions.roles && userPermissions.roles.length > 0) {
          getRolePermissions({
            roleIds: userPermissions.roles.map((role) => role.roleId)
          });
        }
      }
    }, [userPermissions, userId]);

    useEffect(() => {
      if (onChangeLoading) {
        onChangeLoading(isLoadingUpdate);
      }
    }, [onChangeLoading, isLoadingUpdate]);

    const permissions: PermissionCompactDto[] = Form.useWatch('permissions', form) || [];
    const roles: RoleCompactDto[] = Form.useWatch('roles', form) || [];

    const handleChangePermissions = (value: PermissionCompactDto[]) => {
      form.setFieldValue('permissions', value);
    };

    const handleChangeRoles = (value: RoleCompactDto[]) => {
      form.setFieldValue('roles', value);
      getRolePermissions({
        roleIds: value.length > 0 ? value.map((role) => role.roleId) : [0]
      });
    };

    const tabItems: TabsProps['items'] = [
      {
        key: TABS.ROLES,
        label: rolesMessages.title
      },
      {
        key: TABS.PERMISSIONS,
        label: permissionsMessages.title
      }
    ];

    const combinedPermissions = uniqBy(
      [...userGroupPermissions, ...userGroupRolePermissions, ...rolePermissions, ...permissions],
      'permissionId'
    );

    const renderPermissionFromUserGroup = (item: PermissionCompactDto) => {
      const roleIds = userGroupRoles
        ?.filter((role) => role.permissions.map((permission) => permission.permissionId).includes(item.permissionId))
        .map((role) => role.roleId);

      const userGroups =
        userPermissions?.userGroups?.filter(
          (userGroup) =>
            userGroup.roles.map((role) => role.roleId).some((roleId) => roleIds.includes(roleId)) ||
            userGroup.permissions.map((permission) => permission.permissionId).includes(item.permissionId)
        ) ?? [];

      return (
        <Tooltip
          placement='topLeft'
          arrow={{ pointAtCenter: true }}
          title={
            <Typography.Text className='text-xs text-colorTextContrast'>{`${usersMessages.permissionFromGroup} ${userGroups
              .map((userGroup) => userGroup.name)
              .join(', ')}`}</Typography.Text>
          }
        >
          <UserGroupFilledIcon className='text-iconColor' />
        </Tooltip>
      );
    };

    return (
      <Layout className='h-[calc(100vh-80px)]'>
        <Layout.Sider className='min-w-[340px] bg-colorBgBody px-6 pb-5 pt-6'>
          <div>
            <Typography.Title level={4} className='mb-2 text-2.5xl'>
              {userInfo?.fullName}
            </Typography.Title>
            <Typography.Paragraph type='secondary' className='mb-0'>
              {userInfo?.email}
            </Typography.Paragraph>
            <Typography.Paragraph type='secondary' className='mb-8'>
              {userInfo?.organizationUnit?.name}
            </Typography.Paragraph>
          </div>

          <Typography.Title level={5} className='mb-4 text-lg'>
            {usersMessages.grantPermissions}
          </Typography.Title>
          {combinedPermissions.length > 0 && (
            <List
              className='-mx-6 max-h-[calc(100%-136px)] overflow-auto px-6 pb-5'
              dataSource={combinedPermissions}
              rowKey={(item) => item.permissionId}
              split={false}
              renderItem={(item) => (
                <List.Item className='flex gap-x-2 py-[10px]'>
                  <Typography.Paragraph className='mb-0' ellipsis={{ rows: 1 }}>
                    {item.name}
                  </Typography.Paragraph>

                  {userGroupPermissionIds.includes(item.permissionId) ||
                  userGroupRolePermissionIds.includes(item.permissionId)
                    ? renderPermissionFromUserGroup(item)
                    : undefined}
                </List.Item>
              )}
            />
          )}
        </Layout.Sider>
        <Layout className='bg-colorBgContainer pt-6'>
          <div className='relative flex flex-col px-6'>
            <Typography.Title className='mb-2 text-2.5xl'>{usersMessages.permissions}</Typography.Title>
            <Typography.Paragraph type='secondary' className='mb-6'>
              {messages.chooseOneOrMorePerrmission}
            </Typography.Paragraph>
          </div>
          <div className='relative flex h-[calc(100vh)] max-h-[calc(100vh)] flex-col overflow-y-auto px-6'>
            <Form
              scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
              requiredMark={false}
              form={form}
              name='userUpdatePermissionForm'
              onFinish={onFinish}
              layout='horizontal'
              className='mb-16'
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              <Spin spinning={isLoading}>
                <FormItem<UserUpdatePermissionsFormType> name='permissions' noStyle />
                <FormItem<UserUpdatePermissionsFormType> name='roles' noStyle />
                <TabButtons
                  className='pb-4'
                  defaultActiveKey={TABS.PERMISSIONS}
                  items={tabItems}
                  onClick={(key) => setSelectedTab(key as TABS)}
                  activeKey={selectedTab}
                />
                {selectedTab === TABS.PERMISSIONS && (
                  <TreeCheckboxPermissions value={permissions} onChange={handleChangePermissions} />
                )}
                {selectedTab === TABS.ROLES && <CheckboxRoles value={roles} onChange={handleChangeRoles} />}
              </Spin>
            </Form>
            <div className='sticky bottom-0 -mx-6 mt-auto h-20 border-t bg-white px-6'>
              <Button
                onClick={() => form.submit()}
                className='float-right my-5'
                loading={isLoadingUpdate}
                type='primary'
              >
                {messages.saveButtonText}
              </Button>
            </div>
          </div>
        </Layout>
      </Layout>
    );
  }
);
export default UserUpdatePermissionsForm;
